/**
 * In-process RED summary (process lifetime counters; sliding windows via Prometheus scrape).
 */

import {
  DURATION_BUCKET_UPPER_SEC,
  getMetricsStoresForSummary,
  type RedMetricsSummary,
} from './metrics';

function approximateP95Seconds(
  histograms: ReadonlyMap<string, number[]>,
  counts: ReadonlyMap<string, number>
): number {
  const globalBuckets = new Array(DURATION_BUCKET_UPPER_SEC.length).fill(0) as number[];
  let totalCount = 0;

  for (const [key, buckets] of histograms.entries()) {
    const count = counts.get(key) ?? 0;
    totalCount += count;
    for (let i = 0; i < buckets.length; i++) {
      globalBuckets[i] = (globalBuckets[i] ?? 0) + (buckets[i] ?? 0);
    }
  }

  if (totalCount === 0) return 0;

  const target = totalCount * 0.95;
  let cumulative = 0;
  for (let i = 0; i < DURATION_BUCKET_UPPER_SEC.length; i++) {
    cumulative += globalBuckets[i] ?? 0;
    const upper = DURATION_BUCKET_UPPER_SEC[i];
    if (upper === undefined) break;
    if (cumulative >= target) {
      return upper === '+Inf' ? 10 : upper;
    }
  }
  return 10;
}

export function computeRedSummary(): RedMetricsSummary {
  const { requestTotal, durationHistogram } = getMetricsStoresForSummary();

  let totalRequests = 0;
  let requests5xx = 0;
  let requests4xx = 0;
  const durationCounts = new Map<string, number>();

  for (const [key, value] of requestTotal.entries()) {
    totalRequests += value;
    const status = key.split('\0')[2] ?? '0';
    const code = Number.parseInt(status, 10);
    if (code >= 500) requests5xx += value;
    if (code >= 400 && code < 500) requests4xx += value;
    durationCounts.set(key, value);
  }

  return {
    window: 'process_lifetime',
    totalRequests,
    requests5xx,
    requests4xx,
    errorRate5xx: totalRequests > 0 ? requests5xx / totalRequests : 0,
    approximateP95Seconds: approximateP95Seconds(durationHistogram, durationCounts),
  };
}

export type { RedMetricsSummary };
